import * as React from 'react'
import { focusRing } from '@repo/studio-design-system/utilities'
import { useResizeObserver } from '@repo/studio-hooks-dom/use-resize-observer'
import { m } from 'framer-motion'
import { Slot } from 'primitives/slot'
import { Link } from '@repo/react-router-locale/locale-router'
import type { LinkProps } from 'react-router'
import { tw } from 'utils/classnames'

export type NavigationTabsSize = 'regular' | 'compact'

export interface NavigationTabsRootProps {
  className?: string
  children?: React.ReactNode
}

interface NavigationTabsTabBaseProps extends Omit<LinkProps, 'className' | 'children' | 'to'> {
  /** Regular is the page-level size; Compact is for constrained vertical space. @default 'regular' */
  size?: NavigationTabsSize
  /**
   * True when this tab is the one being shown. Drives the selected styling and, when the tab is a
   * link (`to` is set), `aria-current="page"` — set this instead of `data-state`.
   */
  active?: boolean
  disabled?: boolean
  className?: string
  children?: React.ReactNode
  ref?: React.Ref<HTMLAnchorElement>
}

/**
 * Without `to`, an `asChild` tab is not a link, so its child owns the semantics — for example a
 * Radix tab trigger, which announces selection through `aria-selected` instead of `aria-current`.
 */
export type NavigationTabsTabProps = NavigationTabsTabBaseProps &
  ({ asChild?: false; to: LinkProps['to'] } | { asChild: true; to?: LinkProps['to'] })

function NavigationTabsRoot({ className, children }: NavigationTabsRootProps) {
  const listRef = React.useRef<HTMLDivElement>(null)
  const activeTabRef = React.useRef<HTMLElement>(null)
  const [indicator, setIndicator] = React.useState({ x: 0, width: 0 })

  const updateIndicator = React.useCallback(() => {
    const list = listRef.current
    if (!list) {
      return
    }

    const active = list.querySelector<HTMLElement>('[data-state="active"]')
    if (!active) {
      activeTabRef.current = null
      setIndicator(current => (current.width === 0 ? current : { x: 0, width: 0 }))
      return
    }

    const next = { x: active.offsetLeft, width: active.offsetWidth }
    setIndicator(current => (current.x === next.x && current.width === next.width ? current : next))

    if (activeTabRef.current !== active) {
      activeTabRef.current = active
      active.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
    }
  }, [])

  React.useLayoutEffect(updateIndicator, [children, updateIndicator])
  useResizeObserver(listRef, updateIndicator)

  return (
    <div ref={listRef} className={tw('relative inline-flex', className)}>
      {children}
      {indicator.width > 0 ? (
        <m.span
          aria-hidden
          className="pointer-events-none absolute bottom-0 h-0.5 bg-theme_semantic_control_navigation_primary_indicator_color-selected"
          initial={false}
          animate={{ x: indicator.x, width: indicator.width }}
          transition={{ type: 'spring', bounce: 0.15, duration: 0.3 }}
        />
      ) : null}
    </div>
  )
}

function NavigationTabsTab({
  size = 'regular',
  active,
  asChild,
  to,
  disabled,
  ref,
  className,
  children,
  onClick,
  ...props
}: NavigationTabsTabProps) {
  const tabProps = {
    ...props,
    ref,
    'aria-current': active && to !== undefined ? ('page' as const) : undefined,
    'aria-disabled': disabled || undefined,
    'data-state': active ? 'active' : undefined,
    tabIndex: disabled ? -1 : props.tabIndex,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault()
      }
      onClick?.(event)
    },
    className: tw(
      navigationTabsClasses.base,
      navigationTabsClasses.sizes[size],
      disabled && 'cursor-not-allowed',
      className,
    ),
  }

  if (asChild) {
    return <Slot {...tabProps}>{children}</Slot>
  }

  return (
    <Link {...tabProps} to={to}>
      {children}
    </Link>
  )
}

/**
 * NavigationTabs switch between top-level views. Each `Tab` is a React Router `Link` — pass `to`.
 * Wrap the row in `Root` so the selected underline slides between tabs. For in-section view
 * toggling that swaps content rather than navigating, use `SectionTabs`.
 *
 * Mark the tab for the current route with `active`, which sets both the selected styling and
 * `aria-current="page"`. These are links rather than a `tablist`, so `aria-current` is what tells a
 * screen reader where the reader is — do not set `data-state` by hand.
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=2212-22996
 */
export const NavigationTabs = {
  Root: NavigationTabsRoot,
  Tab: NavigationTabsTab,
}

const navigationTabsClasses = {
  base: [
    'inline-flex scroll-mx-3 flex-col items-center whitespace-nowrap outline-none transition-colors',
    'rounded-studio-geometry-semantic-radius-2xs',
    'border-b-2 border-transparent',
    'text-theme_semantic_control_navigation_primary_foreground_color-default',
    '[&:not([aria-disabled=true]):hover:not(:focus-visible):not([data-state=active])]:border-theme_semantic_control_navigation_primary_indicator_color-hover',
    '[&:not([aria-disabled=true]):hover:not(:focus-visible):not([data-state=active])]:text-theme_semantic_control_navigation_primary_foreground_color-hover',
    '[&:not([aria-disabled=true]):active:not([data-state=active])]:border-theme_semantic_control_navigation_primary_indicator_color-active',
    '[&:not([aria-disabled=true]):active:not([data-state=active])]:text-theme_semantic_control_navigation_primary_foreground_color-active',
    '[&:focus-visible:not([data-state=active])]:border-theme_semantic_control_navigation_primary_indicator_color-focus',
    '[&:focus-visible:not([data-state=active])]:text-theme_semantic_control_navigation_primary_foreground_color-focus',
    'data-[state=active]:text-theme_semantic_control_navigation_primary_foreground_color-selected',
    focusRing,
  ].join(' '),
  sizes: {
    regular: 'px-3 pt-3 pb-4 studio-typography-14',
    compact: 'px-3 pt-0.5 pb-2.5 studio-typography-12',
  } satisfies Record<NavigationTabsSize, string>,
}
